// --- NotificationsPanel.jsx (UPDATED FULL FILE with themed icons & borders) ---
import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronDown, ChevronUp, Bot, Zap, Rss, Bell } from "lucide-react";
// Live alerts are supplied by parent; no fetching here

// ---- helpers for theming ----
const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
};
const rgba = (hex, a) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// match AgentKai card themes
const AGENT_THEMES = {
  spend: { accent: "#d4a04f", header: "#2d1b0f" },
  perf: { accent: "#4a9999", header: "#0c1b17" },
  market: { accent: "#b366b3", header: "#311229" },
};
const getTheme = (id) =>
  AGENT_THEMES[id] || { accent: "#8b8b8b", header: "#111213" };

const Icon = ({ name }) => {
  const common = { width: 18, height: 18, strokeWidth: 1.8 };
  if (name === "spend") return <Bot {...common} />;
  if (name === "perf") return <Zap {...common} />;
  if (name === "market") return <Rss {...common} />;
  return null;
};

const emphasizeChange = (text) => {
  // Use non-capturing group for decimals to avoid duplicate fragments
  const parts = String(text).split(/([+-]?\d+(?:\.\d+)?%)/);
  return parts.map((p, i) => {
    if (/^[+-]?\d+(\.\d+)?%$/.test(p)) {
      const isNeg = p.trim().startsWith("-");
      const color = isNeg ? "#ef4444" : "#22c55e";
      return (
        <span key={i} style={{ color, fontWeight: 800 }}>
          {p}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
};

// Emphasize percentages (same as above) AND whole numbers in green/bold for perf
const emphasizePerf = (text) => {
  const parts = String(text).split(/([+-]?\d+(?:\.\d+)?%)/);
  const nodes = [];
  parts.forEach((p, i) => {
    if (/^[+-]?\d+(?:\.\d+)?%$/.test(p)) {
      const isNeg = p.trim().startsWith("-");
      const color = isNeg ? "#ef4444" : "#22c55e";
      nodes.push(
        <span key={`pct-${i}`} style={{ color, fontWeight: 800 }}>
          {p}
        </span>
      );
    } else {
      // Within non-percentage text, wrap whole numbers
      const inner = p.split(/(\b\d+\b)/);
      inner.forEach((t, j) => {
        if (/^\d+$/.test(t)) {
          nodes.push(
            <span key={`n-${i}-${j}`} style={{ color: "#22c55e", fontWeight: 800 }}>
              {t}
            </span>
          );
        } else if (t) {
          nodes.push(<span key={`t-${i}-${j}`}>{t}</span>);
        }
      });
    }
  });
  return nodes;
};

// Clean market intel sentences by removing markdown links and trailing parentheses
const cleanMarketText = (text) => {
  if (!text) return text;
  let s = String(text);
  // remove markdown links [label](url)
  s = s.replace(/\[.*?\]\(.*?\)/g, "");
  // remove any leftover empty parentheses
  s = s.replace(/\s*\(\s*\)\s*/g, " ");
  // collapse spaces and fix space before period
  s = s.replace(/\s{2,}/g, " ").replace(/\s+\./g, ".").trim();
  return s;
};

const defaultGroups = [
  {
    id: "spend",
    title: "Spend Analytics Agent",
    icon: "spend",
    items: [
      { text: "Spend of Minerals increases +12%", time: "15:37" },
      { text: "Unusual spike detected in logistics spend +10%", time: "15:37" },
      {
        text: "Raw material costs for metals increased by +6.5%",
        time: "15:37",
      },
    ],
  },
  {
    id: "perf",
    title: "Supplier Performance Agent",
    icon: "perf",
    items: [
      { text: "Spend of Minerals increases +12%", time: "15:37" },
      { text: "Unusual spike detected in logistics spend +10%", time: "15:37" },
      {
        text: "Raw material costs for metals increased by +6.5%",
        time: "15:37",
      },
    ],
  },
  {
    id: "market",
    title: "Market Intelligence Agent",
    icon: "market",
    items: [
      { text: "Spend of Minerals increases +12%", time: "15:37" },
      { text: "Unusual spike detected in logistics spend +10%", time: "15:37" },
      {
        text: "Raw material costs for metals increased by +6.5%",
        time: "15:37",
      },
    ],
  },
];


const NotificationsPanel = ({
  open,
  onClose,
  groups = defaultGroups,
  alerts,
  loading = false,
  error = null,
}) => {
  const [expanded, setExpanded] = useState(
    () => new Set(groups.map((g) => g.id))
  );
  // Loading and error come from parent

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  const toggle = (id) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  // no fetching here

  // Merge live alerts into provided groups (only spend, perf, market)
  const mergedGroups = useMemo(() => {
    const ts = alerts?.generated_at ? new Date(alerts.generated_at) : null;
    const timeStr = ts
      ? ts
          .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          .replace(/^0/, "")
      : "";
    const mapToItems = (arr) =>
      (arr || []).map((t) => {
        if (typeof t === "string") return { text: t, time: timeStr };
        if (t && typeof t === "object")
          return { text: t.text ?? String(t), time: t.time ?? timeStr };
        return { text: String(t), time: timeStr };
      });

    const liveById = {
      spend: mapToItems(alerts?.spend || alerts?.spend_alerts || []),
      perf: mapToItems(alerts?.perf || alerts?.risk_perf_alerts || []),
      market: mapToItems(alerts?.market || alerts?.market_intel_alerts || []),
    };

    return groups.map((g) => {
      const liveItems = liveById[g.id] || [];
      const hasLive = !loading && !error && liveItems.length > 0;
      return hasLive ? { ...g, items: liveItems } : g;
    });
  }, [groups, alerts, loading, error]);

  // skeleton styles
  const skeletonKeyframes = `
    @keyframes skeletonShimmer {
      0% { background-position: -200px 0; }
      100% { background-position: calc(200px + 100%) 0; }
    }
  `;
  const skeletonRow = {
    height: 14,
    borderRadius: 8,
    background: "#1a1b1e",
    backgroundImage:
      "linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.14), rgba(255,255,255,0.04))",
    backgroundSize: "200px 100%",
    animation: "skeletonShimmer 1.2s ease-in-out infinite",
  };
  const skeletonChip = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14,
    marginTop: 10,
  };

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    backdropFilter: "blur(2px)",
    opacity: open ? 1 : 0,
    pointerEvents: open ? "auto" : "none",
    transition: "opacity .2s ease",
    zIndex: 60,
  };

  const panelStyle = {
    position: "fixed",
    top: 0,
    right: 0,
    height: "100vh",
    width: 420,
    maxWidth: "90vw",
    background: "#0f1011",
    color: "white",
    boxShadow:
      "0 10px 40px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,0.06) inset",
    transform: open ? "translateX(0)" : "translateX(105%)",
    transition: "transform .25s ease",
    zIndex: 61,
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid rgba(255,255,255,0.08)",
  };

  const headerStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 16px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  };

  const titleStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 16,
    fontWeight: 600,
    color: "#FFFFFF",
  };

  const closeBtn = {
    width: 36,
    height: 36,
    display: "grid",
    placeItems: "center",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.06)",
    color: "#FFFFFFCC",
    cursor: "pointer",
  };

  const chipRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14,
    fontSize: 13.5,
    marginTop: 10,
  };
  const chipTime = {
    color: "rgba(255,255,255,0.5)",
    fontSize: 12,
    paddingLeft: 10,
    whiteSpace: "nowrap",
  };

  // dynamic section styles using theme (accent color)
  const sectionStyle = (accent) => ({
    padding: "14px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    borderLeft: `3px solid ${rgba(accent, 0.9)}`, // themed border (as in design)
    background: `
      radial-gradient(220px 120px at 0% -20%, ${rgba(
        accent,
        0.1
      )} 0%, transparent 70%),
      linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))
    `,
  });

  const iconWrap = (accent) => ({
    width: 28,
    height: 28,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    border: `1px solid ${rgba(accent, 0.65)}`, // themed border for icon box
    background: `
      linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.3)),
      radial-gradient(60px 50px at 50% -10%, ${rgba(
        accent,
        0.25
      )} 0%, transparent 70%)
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    color: accent, // icon stroke inherits this
  });

  const sectionHeader = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  };

  const sectionLeft = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 600,
    color: "#FFFFFF",
  };

  return (
    <>
      <div style={overlayStyle} onClick={onClose} />
      <aside role="dialog" aria-modal="true" style={panelStyle}>
        <div style={headerStyle}>
          <div style={titleStyle}>
            {/* <Bell width={18} height={18} /> */}
            Key Alerts
          </div>
          <button aria-label="Close" style={closeBtn} onClick={onClose}>
            <X width={16} height={16} />
          </button>
        </div>

        <div style={{ paddingBottom: 16, overflowY: "auto" }}>
          <style>{skeletonKeyframes}</style>
          {error && !loading && (
            <div style={{ padding: "12px 16px", color: "#ef4444" }}>
              {error}
            </div>
          )}
          {mergedGroups.map((g) => {
            const isOpen = expanded.has(g.id);
            const { accent } = getTheme(g.id);
            return (
              <div key={g.id} style={sectionStyle(accent)}>
                <div style={sectionHeader} onClick={() => toggle(g.id)}>
                  <div style={sectionLeft}>
                    <span style={iconWrap(accent)}>
                      <Icon name={g.icon} />
                    </span>
                    {g.title}
                  </div>
                  {isOpen ? (
                    <ChevronUp width={16} height={16} />
                  ) : (
                    <ChevronDown width={16} height={16} />
                  )}
                </div>

                {isOpen && loading && (
                  <>
                    {[0, 1, 2].map((i) => (
                      <div key={i} style={skeletonChip}>
                        <div style={{ ...skeletonRow, width: "78%" }} />
                        <div style={{ ...skeletonRow, width: 54 }} />
                      </div>
                    ))}
                  </>
                )}

                {isOpen && !loading &&
                  g.items.map((a, i) => {
                    const text = g.id === "market" ? cleanMarketText(a.text) : a.text;
                    const content = g.id === "perf" ? emphasizePerf(text) : emphasizeChange(text);
                    return (
                      <div key={i} style={chipRow}>
                        <span style={{ color: "rgba(255,255,255,0.92)" }}>
                          {content}
                        </span>
                        <span style={chipTime}>{a.time}</span>
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
};

export default NotificationsPanel;

// // --- NotificationsPanel.jsx (FULL FILE) ---
// import React, { useEffect, useState } from "react";
// import { X, ChevronDown, ChevronUp, Bot, Zap, Rss, Award, Bell } from "lucide-react";

// const Icon = ({ name }) => {
//   const common = { width: 18, height: 18, strokeWidth: 1.8 };
//   if (name === "spend") return <Bot {...common} />;
//   if (name === "perf") return <Zap {...common} />;
//   if (name === "market") return <Rss {...common} />;
//   if (name === "scenario") return <Award {...common} />;
//   return null;
// };

// const emphasizeChange = (text) => {
//   const parts = text.split(/([+-]?\d+(\.\d+)?%)/);
//   return parts.map((p, i) => {
//     if (/^[+-]?\d+(\.\d+)?%$/.test(p)) {
//       const isNeg = p.trim().startsWith("-");
//       const color = isNeg ? "#ef4444" : "#22c55e";
//       return (
//         <span key={i} style={{ color, fontWeight: 800 }}>
//           {p}
//         </span>
//       );
//     }
//     return <span key={i}>{p}</span>;
//   });
// };

// const defaultGroups = [
//   {
//     id: "spend",
//     title: "Spend Analytics Agent",
//     icon: "spend",
//     items: [
//       { text: "Spend of Minerals increases +12%", time: "15:37" },
//       { text: "Unusual spike detected in logistics spend +10%", time: "15:37" },
//       { text: "Raw material costs for metals increased by +6.5%", time: "15:37" },
//     ],
//   },
//   {
//     id: "perf",
//     title: "Supplier Performance Agent",
//     icon: "perf",
//     items: [
//       { text: "Spend of Minerals increases +12%", time: "15:37" },
//       { text: "Unusual spike detected in logistics spend +10%", time: "15:37" },
//       { text: "Raw material costs for metals increased by +6.5%", time: "15:37" },
//     ],
//   },
//   {
//     id: "market",
//     title: "Market Intelligence Agent",
//     icon: "market",
//     items: [
//       { text: "Spend of Minerals increases +12%", time: "15:37" },
//       { text: "Unusual spike detected in logistics spend +10%", time: "15:37" },
//       { text: "Raw material costs for metals increased by +6.5%", time: "15:37" },
//     ],
//   },
//   {
//     id: "scenario",
//     title: "Scenario Planner",
//     icon: "scenario",
//     items: [
//       { text: "Spend of Minerals increases +12%", time: "15:37" },
//       { text: "Unusual spike detected in logistics spend +10%", time: "15:37" },
//       { text: "Raw material costs for metals increased by +6.5%", time: "15:37" },
//     ],
//   },
// ];

// const NotificationsPanel = ({ open, onClose, groups = defaultGroups }) => {
//   const [expanded, setExpanded] = useState(() => new Set(groups.map(g => g.id)));

//   useEffect(() => {
//     const onEsc = (e) => e.key === "Escape" && onClose?.();
//     if (open) window.addEventListener("keydown", onEsc);
//     return () => window.removeEventListener("keydown", onEsc);
//   }, [open, onClose]);

//   const toggle = (id) => {
//     const next = new Set(expanded);
//     next.has(id) ? next.delete(id) : next.add(id);
//     setExpanded(next);
//   };

//   const overlayStyle = {
//     position: "fixed",
//     inset: 0,
//     background: "rgba(0,0,0,0.45)",
//     backdropFilter: "blur(2px)",
//     opacity: open ? 1 : 0,
//     pointerEvents: open ? "auto" : "none",
//     transition: "opacity .2s ease",
//     zIndex: 60,
//   };

//   const panelStyle = {
//     position: "fixed",
//     top: 0,
//     right: 0,
//     height: "100vh",
//     width: 420,
//     maxWidth: "90vw",
//     background: "#0f1011",
//     color: "white",
//     boxShadow: "0 10px 40px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,0.06) inset",
//     transform: open ? "translateX(0)" : "translateX(105%)",
//     transition: "transform .25s ease",
//     zIndex: 61,
//     display: "flex",
//     flexDirection: "column",
//     borderLeft: "1px solid rgba(255,255,255,0.08)",
//   };

//   const headerStyle = {
//     position: "sticky",
//     top: 0,
//     zIndex: 1,
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     padding: "16px 16px",
//     background:
//       "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))",
//     borderBottom: "1px solid rgba(255,255,255,0.06)",
//   };

//   const titleStyle = {
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     fontSize: 16,
//     fontWeight: 600,
//     color: "#FFFFFF",
//   };

//   const closeBtn = {
//     width: 36,
//     height: 36,
//     display: "grid",
//     placeItems: "center",
//     borderRadius: 10,
//     border: "1px solid rgba(255,255,255,0.18)",
//     background: "rgba(255,255,255,0.06)",
//     color: "#FFFFFFCC",
//     cursor: "pointer",
//   };

//   const sectionStyle = {
//     padding: "14px 16px",
//     borderBottom: "1px solid rgba(255,255,255,0.06)",
//   };

//   const sectionHeader = {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     cursor: "pointer",
//   };

//   const sectionLeft = {
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     fontWeight: 600,
//     color: "#FFFFFF",
//   };

//   const chipRow = {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 10,
//     padding: "12px 14px",
//     background: "rgba(255,255,255,0.04)",
//     border: "1px solid rgba(255,255,255,0.06)",
//     borderRadius: 14,
//     fontSize: 13.5,
//     marginTop: 10,
//   };
//   const chipTime = { color: "rgba(255,255,255,0.5)", fontSize: 12, paddingLeft: 10, whiteSpace: "nowrap" };

//   return (
//     <>
//       <div style={overlayStyle} onClick={onClose} />
//       <aside role="dialog" aria-modal="true" style={panelStyle}>
//         <div style={headerStyle}>
//           <div style={titleStyle}>
//             <Bell width={18} height={18} />
//             Key Alerts
//           </div>
//           <button aria-label="Close" style={closeBtn} onClick={onClose}>
//             <X width={16} height={16} />
//           </button>
//         </div>

//         <div style={{ paddingBottom: 16, overflowY: "auto" }}>
//           {groups.map((g) => {
//             const isOpen = expanded.has(g.id);
//             return (
//               <div key={g.id} style={sectionStyle}>
//                 <div style={sectionHeader} onClick={() => toggle(g.id)}>
//                   <div style={sectionLeft}>
//                     <span
//                       style={{
//                         width: 28,
//                         height: 28,
//                         display: "grid",
//                         placeItems: "center",
//                         borderRadius: 8,
//                         border: "1px solid rgba(255,255,255,0.12)",
//                         background:
//                           "linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.3))",
//                         color: "#FFFFFFCC",
//                       }}
//                     >
//                       <Icon name={g.icon} />
//                     </span>
//                     {g.title}
//                   </div>
//                   {isOpen ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
//                 </div>

//                 {isOpen &&
//                   g.items.map((a, i) => (
//                     <div key={i} style={chipRow}>
//                       <span style={{ color: "rgba(255,255,255,0.92)" }}>
//                         {emphasizeChange(a.text)}
//                       </span>
//                       <span style={chipTime}>{a.time}</span>
//                     </div>
//                   ))}
//               </div>
//             );
//           })}
//         </div>
//       </aside>
//     </>
//   );
// };

// export default NotificationsPanel;
